// drawer/ThreatIntelDrawer.tsx
import { BellAlertIcon } from '@heroicons/react/24/outline';
import { Dropdown, Tabs } from '@praetorian-chariot/ui';
import { Suspense, useState, useTransition } from 'react';
import { useSearchParams } from 'react-router';

import { CopyToClipboard } from '@/components/CopyToClipboard';
import { OverflowText } from '@/components/OverflowText';
import { Container } from '@/components/ui';
import { useSendThreatNotification } from '@/hooks/useRisks';
import { ThreatTag } from '@/sections/threats/components';
import { THREAT_INTEL_TABS } from '@/sections/threats/constants';
import { useThreatIntelDrawerData } from '@/sections/threats/hooks';
import { parseEnrichment } from '@/sections/threats/utils';
import { useAuth } from '@/state/auth';
import { useGlobalState } from '@/state/global.state';
import { cn } from '@/utils/classname';

import { NotificationModal } from './components';
import { ExploitationTimelineTab, MitreTab, OverviewTab } from './tabs';

type NotificationAction = 'acknowledge' | 'acknowledge-scanning' | null;

/**
 * Loading skeleton for tab content
 */
function TabContentSkeleton() {
  return (
    <div className="animate-pulse space-y-4">
      <div className="bg-chariot-background-secondary h-32 rounded-lg" />
      <div className="bg-chariot-background-secondary h-48 rounded-lg" />
    </div>
  );
}

/**
 * Main threat intelligence drawer component
 * Uses React 19 patterns: Suspense, useTransition
 */
export function ThreatIntelDrawer() {
  const { drawerState } = useGlobalState();
  const { threatIntelDrawerKey } = drawerState;
  const { vulnerability: threatIntel } = useThreatIntelDrawerData(
    threatIntelDrawerKey || ''
  );
  const enriched = parseEnrichment(threatIntel);
  const { name = '' } = enriched ?? {};

  const [searchParams, setSearchParams] = useSearchParams();
  const [isPending, startTransition] = useTransition();
  const { mutate: sendThreatNotification, status: sendThreatStatus } =
    useSendThreatNotification();
  const { isPraetorianUser } = useAuth();

  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const [pendingAction, setPendingAction] = useState<NotificationAction>(null);

  const selectedTabValue = searchParams.get('threat-intel-drawer-tab');
  const selectedTab =
    THREAT_INTEL_TABS.find(tab => tab.value === selectedTabValue) ||
    THREAT_INTEL_TABS[0];

  const handleTabSelect = (tab: { value?: string }) => {
    startTransition(() => {
      setSearchParams(prev => {
        if (tab.value) {
          prev.set('threat-intel-drawer-tab', tab.value);
        } else {
          prev.delete('threat-intel-drawer-tab');
        }
        return prev;
      });
    });
  };

  const handleConfirmNotification = () => {
    if (!threatIntel || !pendingAction) return;

    sendThreatNotification({
      vulnerability: {
        ...threatIntel,
        praetorianAcknowledged: true,
        ...(pendingAction === 'acknowledge-scanning' && {
          praetorianScanned: true,
        }),
      },
    });

    setShowConfirmModal(false);
    setPendingAction(null);
  };

  return (
    <div
      data-testid="threat-intel-drawer"
      className="flex size-full flex-col gap-6 overflow-auto p-6"
    >
      <h1
        data-testid="threat-intel-drawer-name"
        className="text-chariot-text-primary text-xl font-bold"
      >
        <CopyToClipboard alwaysShowCopyIcon>
          <OverflowText text={name} />
        </CopyToClipboard>
      </h1>

      <ThreatTag vulnerability={enriched} />

      {isPraetorianUser && threatIntel && (
        <div className="flex gap-2">
          <Dropdown
            placeholder="Send Notification"
            icon={<BellAlertIcon className="size-4" />}
            styleType="rounded"
            size="xs"
            options={[
              {
                label: 'Acknowledge Threat',
                value: 'acknowledge',
                onClick: () => {
                  setPendingAction('acknowledge');
                  setShowConfirmModal(true);
                },
                disabled: sendThreatStatus === 'pending',
              },
              {
                label: 'Acknowledge + Scanning',
                value: 'scan',
                onClick: () => {
                  setPendingAction('acknowledge-scanning');
                  setShowConfirmModal(true);
                },
                disabled: sendThreatStatus === 'pending',
              },
            ]}
            listClassName="right-0"
          />
        </div>
      )}

      {/* React 19: Show isPending state during tab transitions */}
      <Container
        className={cn(
          'flex flex-col gap-6 transition-opacity duration-150',
          isPending && 'opacity-50'
        )}
      >
        <Tabs
          tabs={[...THREAT_INTEL_TABS]}
          selected={selectedTab}
          variant="tertiary"
          onSelect={handleTabSelect}
        />

        {/* React 19: Suspense boundaries for declarative loading states */}
        <Suspense fallback={<TabContentSkeleton />}>
          {selectedTab.value === '' && <OverviewTab threatIntel={enriched} />}
        </Suspense>
        <Suspense fallback={<TabContentSkeleton />}>
          {selectedTab.value === 'exploitation-timeline' && (
            <ExploitationTimelineTab threatIntel={enriched} />
          )}
        </Suspense>
        <Suspense fallback={<TabContentSkeleton />}>
          {selectedTab.value === 'mitre' && <MitreTab threatIntel={enriched} />}
        </Suspense>
      </Container>

      <NotificationModal
        open={showConfirmModal}
        onClose={() => {
          setShowConfirmModal(false);
          setPendingAction(null);
        }}
        onConfirm={handleConfirmNotification}
        isPending={sendThreatStatus === 'pending'}
        actionType={pendingAction}
      />
    </div>
  );
}
